import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface ExportHeroSectionProps {
  onContactClick: () => void;
}

const ExportHeroSection = ({ onContactClick }: ExportHeroSectionProps) => {
  const { t } = useTranslation('home');

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/e909a735869d9b7c901a437bac5bba7d.jpg"
          alt="The Daeg International Trade - Xuất khẩu hàng Châu Á sang Baltics và Bắc Âu"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 text-center">
        <div className="mb-4 sm:mb-6">
          <span className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-full text-xs sm:text-sm text-amber-300 font-medium backdrop-blur-sm">
            <i className="ri-global-line mr-1.5"></i>{t('hero.badge')}
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 font-serif leading-tight">
          {t('hero.title.line1')}{t('hero.title.highlight1')}
          <br />
          {t('hero.title.line2')}{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-500">{t('hero.title.highlight2')}</span>
        </h1>

        <p className="text-sm sm:text-base md:text-xl text-gray-300 max-w-3xl mx-auto mb-6 sm:mb-8 leading-relaxed px-2">
          {t('hero.description')}
        </p>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 max-w-4xl mx-auto mb-8 sm:mb-10">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-amber-500/20">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-1 sm:mb-2">30M+</div>
            <div className="text-xs sm:text-sm text-gray-300">{t('hero.stats.population')}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-amber-500/20">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-1 sm:mb-2">5+</div>
            <div className="text-xs sm:text-sm text-gray-300">{t('hero.stats.experience')}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-amber-500/20">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-1 sm:mb-2">2</div>
            <div className="text-xs sm:text-sm text-gray-300">{t('hero.stats.locations')}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-amber-500/20">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-1 sm:mb-2">100%</div>
            <div className="text-xs sm:text-sm text-gray-300">{t('hero.stats.support')}</div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4 sm:px-0">
          <button
            onClick={onContactClick}
            className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-amber-500 to-yellow-600 text-black text-base sm:text-lg font-bold rounded-lg hover:from-amber-600 hover:to-yellow-700 transition-all shadow-lg shadow-amber-500/50 whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta.contact')}
          </button>
          <a
            href="#services"
            className="w-full sm:w-auto text-center px-6 sm:px-8 py-3.5 sm:py-4 bg-white/10 backdrop-blur-sm text-white text-base sm:text-lg font-semibold rounded-lg border-2 border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta.learn')}
          </a>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-2xl sm:text-3xl text-amber-400"></i>
      </div>
    </section>
  );
};

export default ExportHeroSection;